import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, Users, AlertTriangle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSuperadminDashboard, useSuperadminAuditLog } from '@/hooks/useSuperadmin';
import { format, formatDistanceToNow } from 'date-fns';

interface ErrorEntry {
  activity_id: string;
  action_type: string;
  page_path: string;
  created_at: string;
  user_email: string;
  tenant_slug: string;
  tenant_name: string;
}

const SuperadminMonitoring = () => {
  const { tenants, activity, loading: dashLoading } = useSuperadminDashboard();
  const { entries: auditEntries, loading: auditLoading } = useSuperadminAuditLog();
  const [errors, setErrors] = useState<ErrorEntry[]>([]);
  const [errLoading, setErrLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [alerts, setAlerts] = useState<Array<{ id: string; title: string; detail: string | null; severity: string; created_at: string }>>([]);

  useEffect(() => {
    supabase.from('superadmin_alerts')
      .select('id, title, detail, severity, created_at')
      .is('resolved_at', null)
      .order('created_at', { ascending: false })
      .then(({ data }) => setAlerts(data ?? []));
  }, []);

  const loadErrors = async () => {
    setErrLoading(true);
    const { data } = await supabase.rpc('get_sa_activity_feed', { p_limit: 50 });
    const all = (data ?? []) as ErrorEntry[];
    setErrors(all.filter(e => e.action_type === 'error' || e.action_type === 'warning'));
    setErrLoading(false);
    setLastRefresh(new Date());
  };

  useEffect(() => { loadErrors(); }, []);

  // Derived metrics
  const totalActive   = tenants.reduce((s, t) => s + Number(t.mau), 0);
  const totalSessions = tenants.reduce((s, t) => s + Number(t.wau), 0);
  const recentActivity = activity.slice(0, 10);

  const actionColor: Record<string, string> = {
    login:         'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    logout:        'bg-muted text-muted-foreground',
    feature_usage: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    error:         'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    warning:       'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  };

  if (dashLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Open superadmin alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a) => (
            <div
              key={a.id}
              className={`rounded-md border p-3 text-sm ${a.severity === 'critical' ? 'border-destructive text-destructive' : 'border-warning'}`}
            >
              <div className="font-medium">{a.title}</div>
              {a.detail && <div className="text-muted-foreground">{a.detail}</div>}
              <button
                type="button"
                className="mt-1 text-xs underline"
                onClick={async () => {
                  await supabase.from('superadmin_alerts').update({ resolved_at: new Date().toISOString() }).eq('id', a.id);
                  setAlerts((prev) => prev.filter((x) => x.id !== a.id));
                }}
              >
                dismiss
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Platform Monitoring</h1>
            <p className="text-muted-foreground text-sm">
              Cross-tenant health · Last refreshed {formatDistanceToNow(lastRefresh, { addSuffix: true })}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={loadErrors}>
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Tenants</p>
              <p className="text-3xl font-bold mt-1">{tenants.length}</p>
            </div>
            <CheckCircle2 className="h-6 w-6 text-emerald-500" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Monthly Active Users</p>
              <p className="text-3xl font-bold mt-1">{totalActive}</p>
            </div>
            <Users className="h-6 w-6 text-muted-foreground" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Weekly Sessions</p>
              <p className="text-3xl font-bold mt-1">{totalSessions}</p>
            </div>
            <Activity className="h-6 w-6 text-muted-foreground" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Errors (30d)</p>
              <p className={`text-3xl font-bold mt-1 ${errors.length > 0 ? 'text-red-500' : ''}`}>{errors.length}</p>
            </div>
            <AlertTriangle className={`h-6 w-6 ${errors.length > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Per-tenant health table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Tenant Health</CardTitle>
            <CardDescription>MAU and weekly session counts per organisation</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-4 py-2 text-start text-xs font-medium text-muted-foreground uppercase">Org</th>
                  <th className="px-4 py-2 text-start text-xs font-medium text-muted-foreground uppercase">Plan</th>
                  <th className="px-4 py-2 text-end text-xs font-medium text-muted-foreground uppercase">MAU</th>
                  <th className="px-4 py-2 text-end text-xs font-medium text-muted-foreground uppercase">WAU</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tenants.map(t => (
                  <tr key={t.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2">
                      <p className="font-medium">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.slug}</p>
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-xs capitalize text-muted-foreground">{t.plan}</span>
                    </td>
                    <td className="px-4 py-2 text-end font-mono text-sm">{t.mau}</td>
                    <td className="px-4 py-2 text-end font-mono text-sm">{t.wau}</td>
                  </tr>
                ))}
                {tenants.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground text-sm">No tenants yet</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Live activity feed */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Live Activity Feed</CardTitle>
            <CardDescription>Most recent 10 cross-tenant events</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentActivity.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No activity recorded yet</p>
            ) : (
              recentActivity.map(a => (
                <div key={a.activity_id} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium mt-0.5 whitespace-nowrap ${actionColor[a.action_type] ?? actionColor.feature_usage}`}>
                    {a.action_type}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{a.user_email}</p>
                    <p className="text-xs text-muted-foreground">{a.tenant_name} · {a.page_path}</p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}
                  </span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Error log */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Error & Warning Log
          </CardTitle>
          <CardDescription>Filtered from activity feed — action_type = error | warning</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {errLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : errors.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-muted-foreground">
              <CheckCircle2 className="h-8 w-8 mb-3 text-emerald-500 opacity-70" />
              <p className="text-sm">No errors or warnings recorded</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Type</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">User</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Tenant</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Path</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {errors.map(e => (
                  <tr key={e.activity_id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-3">
                      <Badge className={`text-xs ${actionColor[e.action_type]}`}>{e.action_type}</Badge>
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">{e.user_email}</td>
                    <td className="px-6 py-3 text-muted-foreground">{e.tenant_name}</td>
                    <td className="px-6 py-3 font-mono text-xs text-muted-foreground">{e.page_path}</td>
                    <td className="px-6 py-3 text-muted-foreground text-xs">
                      {format(new Date(e.created_at), 'MMM d, HH:mm')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Superadmin audit log */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Superadmin Actions</CardTitle>
          <CardDescription>Audit trail of tenant/user management actions</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {auditLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : auditEntries.length === 0 ? (
            <div className="px-6 py-10 text-center text-muted-foreground text-sm">No actions recorded yet</div>
          ) : (
            <div className="divide-y divide-border">
              {auditEntries.map((e) => (
                <div key={e.id} className="px-6 py-3 flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium">{e.actor_email ?? 'Unknown'}</span>
                    <span className="text-muted-foreground"> — {e.action.replace(/_/g, ' ').replace(/\./g, ': ')}</span>
                    {(e.target_tenant_name || e.target_user_email) && (
                      <span className="text-muted-foreground">
                        {' '}({[e.target_tenant_name, e.target_user_email].filter(Boolean).join(', ')})
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(e.created_at), { addSuffix: true })}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperadminMonitoring;
